import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Platform, type NativeSyntheticEvent, type TextInputSelectionChangeEventData} from 'react-native';
import type {InputValue} from '@lookiero/aurora';

import {DEFAULT_DATE_FORMAT, type DateFormat, type DateSegment} from '@helpers';

import {
  applyRawChange,
  buildLayout,
  caretToOffset,
  fromISO,
  offsetToCaret,
  padSegment,
  padSegments,
  toISO,
  type Caret,
  type Parts,
  type SelectionRange,
} from './dateMask';

interface UseDateMaskOptions {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  format?: DateFormat;
}

interface UseDateMask {
  displayValue: string;
  selection: SelectionRange | undefined;
  handleChange: (raw: InputValue) => void;
  handleSelectionChange: (event: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => void;
  handleBlur: () => void;
}

export const useDateMask = ({
  value,
  onChange,
  onBlur,
  format = DEFAULT_DATE_FORMAT,
}: UseDateMaskOptions): UseDateMask => {
  const [parts, setParts] = useState<Parts>(() => fromISO(value));
  const [selection, setSelection] = useState<SelectionRange | undefined>(undefined);
  const caretSegment = useRef<DateSegment | null>(null);

  // Local edits emit exactly toISO(parts), so the parent's echoed value never reseeds the state.
  useEffect(() => {
    if ((value ?? '') !== toISO(parts)) {
      setParts(fromISO(value));
      caretSegment.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const layout = useMemo(() => buildLayout(parts, format), [parts, format]);

  // Web applies `selection` once per object identity, but native re-imposes it on every cursor
  // move; release it once it has landed so the user keeps control of the caret.
  useEffect(() => {
    if (Platform.OS !== 'web' && selection) setSelection(undefined);
  }, [selection]);

  // Fresh parts/selection identities force a re-render that resyncs the DOM even when an edit is rejected.
  const commit = useCallback(
    (next: Parts, caret: Caret): void => {
      const offset = caretToOffset(caret, buildLayout(next, format));
      caretSegment.current = caret.segment;
      setParts(next);
      setSelection({start: offset, end: offset});
      onChange(toISO(next));
    },
    [format, onChange],
  );

  const handleChange = useCallback(
    (raw: InputValue): void => {
      const {parts: next, caret} = applyRawChange(parts, format, String(raw));
      commit(next, caret);
    },
    [parts, format, commit],
  );

  const handleSelectionChange = useCallback(
    (event: NativeSyntheticEvent<TextInputSelectionChangeEventData>): void => {
      const {start, end} = event.nativeEvent.selection;
      const caret = offsetToCaret(start, layout);
      const previous = caretSegment.current;
      caretSegment.current = caret.segment;

      if (start !== end || !previous || previous === caret.segment) return;
      const padded = padSegment(parts, previous);
      if (padded === parts) return;
      commit(padded, caret);
    },
    [parts, layout, commit],
  );

  // Blur hands the final (padded) value to the consumer so it can validate without relying on
  // its own state having caught up with the onChange emitted in this same tick.
  const handleBlur = useCallback((): void => {
    const padded = padSegments(parts);
    if (padded !== parts) {
      setParts(padded);
      onChange(toISO(padded));
    }
    caretSegment.current = null;
    onBlur?.(toISO(padded));
  }, [parts, onChange, onBlur]);

  return {displayValue: layout.display, selection, handleChange, handleSelectionChange, handleBlur};
};
